import React from 'react';
import { Award, Shield, Heart, GraduationCap, Star } from 'lucide-react';
import './Services.css';
import './Team.css';

function Team() {
  return (
    <>
      <section className="page-hero">
        <div className="page-hero__bg">
          <img src="/images/three-dogs.jpg" alt="Our team" />
          <div className="page-hero__overlay" />
        </div>
        <div className="page-hero__content container">
          <span className="section-label" style={{ color: 'var(--color-accent-light)' }}>About Us</span>
          <h1 className="page-hero__title">Meet the Team</h1>
          <p className="page-hero__subtitle">
            A passionate team of qualified groomers dedicated to providing the highest standard of care.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="services-intro section">
        <div className="container">
          <div className="services-intro__content">
            <p>
              Tails &amp; Tweed Pet Grooming was set up by Laura to provide high quality grooming
              services to Oxfordshire dogs, cats and small animals. Laura has over 25 years
              experience working within the grooming and pet care industry and holds some of the
              highest qualifications available, including the prestigious International Certified
              Master Groomer qualification, which involved over 15 separate exams to obtain.
            </p>
            <p>
              The business has grown from strength to strength and we now also have a dog grooming
              training facility offering City &amp; Guild courses alongside our grooming salon.
            </p>
            <p>
              All of our stylists are City &amp; Guild Qualified, with our apprentices working
              towards certification. All members of the team are qualified in pet specific
              emergency first aid and have received canine and feline behaviour training so you
              can rest assured your pet is in the best hands.
            </p>
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="team section" style={{ paddingTop: 0 }}>
        <div className="container">
          {/* Laura */}
          <div className="team__featured">
            <div className="team__featured-image">
              <img src="/images/salon.jpg" alt="Laura - Founder &amp; Head Stylist" />
            </div>
            <div className="team__featured-content">
              <span className="section-label">Owner &amp; Head Stylist</span>
              <h2 className="section-title">Laura <span className="team__credential">(ICMG)</span></h2>
              <p className="team__text">
                Laura is a City &amp; Guild qualified groomer, an International Master Groomer,
                and multi-award winning stylist with over 25 years experience.
              </p>
              <p className="team__text">
                Her work has produced dogs for some of the most prestigious events in the world
                including Crufts, and she continues to compete internationally in grooming
                competitions &mdash; consistently achieving recognition across multiple categories.
              </p>
              <p className="team__text">
                Laura&rsquo;s passion lies in precision, breed standard grooming, and raising the
                standards of the grooming industry. Alongside running the salon, she is deeply
                committed to mentoring and educating the next generation of groomers.
              </p>
              <p className="team__text">
                Whether she is preparing a show dog, teaching advanced techniques or grooming a
                much-loved family pet, Laura&rsquo;s approach is always the same: structure,
                balance, and welfare.
              </p>
              <div className="team__qualifications">
                <div className="team__qual">
                  <Award size={20} />
                  <span>International Certified Master Groomer</span>
                </div>
                <div className="team__qual">
                  <Star size={20} />
                  <span>Multi-Award Winning Stylist</span>
                </div>
                <div className="team__qual">
                  <GraduationCap size={20} />
                  <span>25+ Years Industry Experience</span>
                </div>
              </div>
            </div>
          </div>

          {/* Darren */}
          <div className="team__featured team__featured--reverse">
            <div className="team__featured-image">
              <img src="/images/gundog.jpg" alt="Darren - Owner &amp; Stylist" />
            </div>
            <div className="team__featured-content">
              <span className="section-label">Owner &amp; Stylist</span>
              <h2 className="section-title">Darren</h2>
              <p className="team__text">
                Darren is a City &amp; Guild qualified groomer with a specialist knowledge in
                traditional terrier handstripping.
              </p>
              <p className="team__text">
                With a deep understanding of coat texture, growth cycles, and breed-correct
                presentation, Darren is known for producing clean, sharp finishes while maintaining
                coat integrity and skin health.
              </p>
              <p className="team__text">
                His calm, methodical approach makes him particularly skilled with terrier breeds
                and wire coated dogs requiring specialist care. He has also prepared dogs for the
                prestigious show, Crufts.
              </p>
              <p className="team__text">
                For owners wanting their dogs presented correctly &mdash; whether for showing or
                simply to preserve coat quality &mdash; Darren&rsquo;s expertise ensures authentic,
                traditional results.
              </p>
              <div className="team__qualifications">
                <div className="team__qual">
                  <GraduationCap size={20} />
                  <span>City &amp; Guild Qualified Groomer</span>
                </div>
                <div className="team__qual">
                  <Award size={20} />
                  <span>Handstripping Specialist</span>
                </div>
              </div>
            </div>
          </div>

          {/* Abi */}
          <div className="team__featured">
            <div className="team__featured-image">
              <img src="/images/cat-lying.jpg" alt="Abi - Head Cat Groomer" />
            </div>
            <div className="team__featured-content">
              <span className="section-label">Head Cat Groomer</span>
              <h2 className="section-title">Abi</h2>
              <p className="team__text">
                Abi brings a unique and valuable dual skill set to the salon. She is both a
                specialist cat groomer and qualified veterinary nurse, with over ten years of
                experience.
              </p>
              <p className="team__text">
                Her veterinary background provides an advanced understanding of feline behaviour,
                health considerations, and safe handling techniques.
              </p>
              <p className="team__text">
                Abi&rsquo;s patient and compassionate approach ensures cats are groomed in a calm,
                controlled environment, with welfare always the top priority. Her clinical knowledge,
                combined with grooming expertise, offers cat owners an added level of reassurance
                and professionalism.
              </p>
              <div className="team__qualifications">
                <div className="team__qual">
                  <Shield size={20} />
                  <span>Qualified Veterinary Nurse</span>
                </div>
                <div className="team__qual">
                  <Heart size={20} />
                  <span>Specialist Cat Groomer</span>
                </div>
                <div className="team__qual">
                  <GraduationCap size={20} />
                  <span>10+ Years Experience</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="team-values section" style={{ background: 'var(--color-cream)' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: 60 }}>
            <span className="section-label">Our Standards</span>
            <h2 className="section-title">What Sets Our Team Apart</h2>
          </div>

          <div className="values-grid">
            <div className="value-card">
              <div className="value-card__icon">
                <GraduationCap size={28} />
              </div>
              <h3 className="value-card__title">City &amp; Guilds Qualified</h3>
              <p className="value-card__text">
                All of our stylists are City &amp; Guilds qualified, with apprentices
                actively working towards their certification under expert guidance.
              </p>
            </div>

            <div className="value-card">
              <div className="value-card__icon">
                <Shield size={28} />
              </div>
              <h3 className="value-card__title">First Aid Trained</h3>
              <p className="value-card__text">
                Every member of our team is qualified in pet-specific emergency first aid,
                ensuring your pet is always in safe hands.
              </p>
            </div>

            <div className="value-card">
              <div className="value-card__icon">
                <Heart size={28} />
              </div>
              <h3 className="value-card__title">Behaviour Trained</h3>
              <p className="value-card__text">
                All team members have received canine and feline behaviour training,
                enabling them to read and respond to your pet&rsquo;s needs.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Team;
